/**
 * Auth state store.
 *
 * Single source of truth for the authenticated user's token and
 * profile. Persists token to localStorage so the WS client can
 * reconnect after reloads. Backend manages the HttpOnly media_token
 * cookie — the frontend only handles the Bearer token.
 *
 * Validates INV-AUTH-14 (WS auth frame) and INV-AUTH-12 (set-password).
 */

import { create } from "zustand";
import {
  getApiBaseUrl,
  getCurrentWorkspace,
  getWorkspaceById,
  LOCAL_WORKSPACE_ID,
  useWorkspaceStore,
} from "./workspaceStore";

const TOKEN_STORAGE_KEY = "magnis-auth-token";
const USER_STORAGE_KEY = "magnis-auth-user";

export interface AuthUser {
  readonly id: string;
  readonly name: string;
  readonly surname: string | null;
  readonly email: string | null;
}

interface LoginPayload {
  readonly email?: string;
  readonly password?: string;
}

interface LoginResponse {
  readonly token: string;
  readonly user: AuthUser;
}

interface AuthState {
  readonly workspaceId: string | null;
  readonly token: string | null;
  readonly user: AuthUser | null;
  login: (payload?: LoginPayload) => Promise<void>;
  logout: () => Promise<void>;
  /** Drop a stale/rejected token LOCALLY (no backend call). The WS client
   *  calls this when the server rejects the presented token so the next
   *  reconnect goes token-less (Open-mode admits the local default user). */
  clearStaleToken: () => void;
  /** Set password for the currently authenticated user (enrolment). */
  setPassword: (password: string) => Promise<void>;
  applySessionForWorkspace: (workspaceId: string, token: string, user: AuthUser) => void;
  applySession: (token: string, user: AuthUser) => void;
}

function storageKey(baseKey: string, workspaceId: string | null): string {
  if (workspaceId == null || workspaceId === LOCAL_WORKSPACE_ID) {
    return baseKey;
  }
  return `${baseKey}:${workspaceId}`;
}

function readStoredToken(workspaceId: string | null): string | null {
  try {
    return localStorage.getItem(storageKey(TOKEN_STORAGE_KEY, workspaceId));
  } catch {
    return null;
  }
}

function readStoredUser(workspaceId: string | null): AuthUser | null {
  try {
    const raw = localStorage.getItem(storageKey(USER_STORAGE_KEY, workspaceId));
    if (raw == null || raw === "") return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

function persistForWorkspace(
  workspaceId: string | null,
  token: string | null,
  user: AuthUser | null,
): void {
  try {
    const tokenKey = storageKey(TOKEN_STORAGE_KEY, workspaceId);
    const userKey = storageKey(USER_STORAGE_KEY, workspaceId);
    if (token === null) {
      localStorage.removeItem(tokenKey);
    } else {
      localStorage.setItem(tokenKey, token);
    }
    if (user === null) {
      localStorage.removeItem(userKey);
    } else {
      localStorage.setItem(userKey, JSON.stringify(user));
    }
  } catch {
    /* storage unavailable (private mode) — keep in-memory state only */
  }
}

function syncWorkspaceAuthState(workspaceId: string | null): void {
  const effectiveWorkspaceId = workspaceId ?? LOCAL_WORKSPACE_ID;
  useAuthStore.setState({
    workspaceId: effectiveWorkspaceId,
    token: readStoredToken(effectiveWorkspaceId),
    user: readStoredUser(effectiveWorkspaceId),
  });
}

export const useAuthStore = create<AuthState>((set, get) => ({
  workspaceId: getCurrentWorkspace()?.id ?? LOCAL_WORKSPACE_ID,
  token: readStoredToken(getCurrentWorkspace()?.id ?? LOCAL_WORKSPACE_ID),
  user: readStoredUser(getCurrentWorkspace()?.id ?? LOCAL_WORKSPACE_ID),

  login: async (payload?: LoginPayload): Promise<void> => {
    const workspaceId =
      get().workspaceId ?? getCurrentWorkspace()?.id ?? LOCAL_WORKSPACE_ID;
    const response = await fetch(`${getApiBaseUrl()}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload ?? {}),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Login failed (${String(response.status)}): ${text.length > 0 ? text : response.statusText}`,
      );
    }
    const data = (await response.json()) as LoginResponse;
    persistForWorkspace(workspaceId, data.token, data.user);
    set({ workspaceId, token: data.token, user: data.user });
  },

  clearStaleToken: (): void => {
    const workspaceId =
      get().workspaceId ?? getCurrentWorkspace()?.id ?? LOCAL_WORKSPACE_ID;
    if (get().token === null) return; // already token-less — nothing to clear
    persistForWorkspace(workspaceId, null, null);
    set({ token: null, user: null });
  },

  logout: async (): Promise<void> => {
    const workspaceId =
      get().workspaceId ?? getCurrentWorkspace()?.id ?? LOCAL_WORKSPACE_ID;

    // Round-7 Codex R6-2: do NOT silently swallow network errors.
    //
    // Previously a network failure (offline, backend down, CORS
    // reject) flipped the UI to "logged out" while the HttpOnly
    // `media_token` cookie remained in the browser, leaving
    // `<img src="/files/X">` requests authenticated on the backend.
    // That stale-auth gap is now surfaced to the caller — local
    // state is cleared ONLY after the backend confirms logout
    // (2xx success) or returns 401 (token already invalid, which
    // is the correct post-condition anyway).
    let response: Response;
    try {
      response = await fetch(`${getApiBaseUrl()}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (err: unknown) {
      // Network-level failure. Leave token + user in place so the UI
      // can re-prompt the user to retry or clear cookies manually.
      throw new Error(
        `Logout failed — network error: ${
          err instanceof Error ? err.message : String(err)
        }. Your session may still be active on this device.`,
      );
    }
    // 401 is a deliberate success path: the token the backend
    // inspected is already invalid, so from the user's point of view
    // they are logged out. Not a silent fallback — the server
    // confirmed the state.
    if (!response.ok && response.status !== 401) {
      const text = await response.text().catch(() => "");
      throw new Error(
        `Logout failed (${String(response.status)}): ${
          text.length > 0 ? text : response.statusText
        }. Your session may still be active on this device.`,
      );
    }
    persistForWorkspace(workspaceId, null, null);
    set({ workspaceId, token: null, user: null });
  },

  setPassword: async (password: string): Promise<void> => {
    const token = get().token;
    if (token === null) throw new Error("not authenticated");
    const response = await fetch(`${getApiBaseUrl()}/api/auth/set-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
      body: JSON.stringify({ password }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Set password failed (${String(response.status)}): ${text.length > 0 ? text : response.statusText}`,
      );
    }
  },

  applySessionForWorkspace: (
    workspaceId: string,
    token: string,
    user: AuthUser,
  ): void => {
    persistForWorkspace(workspaceId, token, user);
    if ((get().workspaceId ?? LOCAL_WORKSPACE_ID) === workspaceId) {
      set({ workspaceId, token, user });
    }
  },

  applySession: (token: string, user: AuthUser): void => {
    const workspaceId =
      get().workspaceId ?? getCurrentWorkspace()?.id ?? LOCAL_WORKSPACE_ID;
    get().applySessionForWorkspace(workspaceId, token, user);
  },
}));

useWorkspaceStore.subscribe((state, prev) => {
  if (!state.ready) return;
  if (
    state.selectedWorkspaceId === prev.selectedWorkspaceId &&
    state.ready === prev.ready
  ) {
    return;
  }
  syncWorkspaceAuthState(state.selectedWorkspaceId);
});

/** Convenience selector for the raw token — used by non-React code. */
export function getAuthToken(): string | null {
  return useAuthStore.getState().token;
}

export function getStoredUserForWorkspace(workspaceId: string): AuthUser | null {
  return readStoredUser(getWorkspaceById(workspaceId)?.id ?? workspaceId);
}

/**
 * Merge the current Bearer token into a `HeadersInit`. If no token is
 * set, `extra` is returned unchanged (no `Authorization` header added).
 * Centralises the "add Bearer if present" pattern that previously lived
 * in three separate call sites (review item, round-3 Simplicity cop).
 */
export function authHeaders(extra?: HeadersInit): HeadersInit {
  const token = getAuthToken();
  if (token === null) return extra ?? {};
  // Merge order: Authorization first, then caller-provided headers.
  // Using the Headers API preserves whatever shape `extra` uses (plain
  // object, array of pairs, or Headers instance).
  const merged = new Headers(extra);
  merged.set("Authorization", `Bearer ${token}`);
  return merged;
}
